import { useState } from "react";
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { PoModalShell } from "./common/PoModalShell";
import { PoButton } from "./common/PoButton";

interface CsvAccountInfoModalProps {
  open: boolean;
  onClose: () => void;
  accountList: {
    email: string;
    account_name: string;
    temp_password: string;
  }[];
  errorHeaderLocation: string;
  onDownloadCsv: () => void;
}

export function CsvAccountInfoModal({
  open,
  onClose,
  accountList,
  errorHeaderLocation,
  onDownloadCsv,
}: CsvAccountInfoModalProps) {
  const [showPassword, setShowPassword] = useState(false);

  const handleClose = () => {
    setShowPassword(false);
    onClose();
  };

  return (
    // cmd-po_1199(T2a): raw Dialog → PoModalShell 移行（precedent=ProductEditModal:1163）
    // ★W2挙動差異(AC差異申告): 旧Dialog は Esc で閉じた（backdrop は onClose reason 判定で無視=閉じない）。
    //   PoModalShell は MUI Modal へ onClose 未配線ゆえ Esc/backdrop で閉じない=Esc-close が失われる。
    //   PoModalShell 17件の標準挙動・footer「閉じる」/× で閉じる経路は handleClose で保全。
    <PoModalShell
      open={open}
      title="登録アカウント情報一覧"
      onClose={handleClose}
      testidPrefix="csv-account-info"
      width="w760"
      footer={
        <>
          {errorHeaderLocation !== "None" && (
            <PoButton
              variant="secondary"
              data-testid="csv-account-error-download"
              // ★殿裁定(alert_20260825_followcall_stop_warning_hex): error色をカタログトークン
              //   var(--sem-error)=#a53229 へ置換（PoButton に error variant 無のため sx で付与）。
              sx={{ color: "var(--sem-error, #a53229)", borderColor: "var(--sem-error, #a53229)" }}
              onClick={() => {
                const link = document.createElement("a");
                link.href = errorHeaderLocation;
                document.body.appendChild(link);
                link.click();
                link.parentNode?.removeChild(link);
              }}
            >
              エラー内容ダウンロード
            </PoButton>
          )}
          <PoButton variant="secondary" onClick={onDownloadCsv} data-testid="csv-account-download">
            CSVダウンロード
          </PoButton>
          <PoButton variant="secondary" onClick={handleClose} data-testid="csv-account-close-button">
            閉じる
          </PoButton>
        </>
      }
    >
      <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
        <Box sx={{ flex: 1, border: "1px solid", borderColor: "var(--color-border-light)",
          borderRadius: "var(--radius-sm)", p: 1.5 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: "var(--st-06-paid)",
            fontFamily: "var(--font-heading, inherit)", lineHeight: 1.2 }}>
            {accountList.length}
          </Typography>
          <Typography variant="caption" sx={{ color: "var(--color-sumi-muted)" }}>
            登録に成功した行
          </Typography>
        </Box>
        {errorHeaderLocation !== "None" && (
          <Box sx={{ flex: 1, border: "1px solid", borderColor: "var(--color-border-light)",
            borderRadius: "var(--radius-sm)", p: 1.5, display: "flex", flexDirection: "column",
            justifyContent: "center" }}>
            <Typography variant="caption" sx={{ color: "var(--color-sumi-muted)" }}>
              エラーあり（詳細はダウンロード）
            </Typography>
          </Box>
        )}
      </Box>
      <Typography variant="body2" sx={{ color: "var(--color-sumi-muted)", mb: 2 }}>
        以下のアカウントが登録されました。初期パスワードを安全な場所に保存してください。
      </Typography>
      {/* ★AC2: stickyHeader アカウント表は verbatim 移設（触らない）。maxHeight:60vh 内側スクロール保持 */}
      <TableContainer component={Paper} sx={{ maxHeight: "60vh", border: "1px solid var(--color-border)" }}>
        <Table stickyHeader size="small" sx={{ borderCollapse: "collapse" }} data-testid="csv-account-table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontSize: "var(--fs-11)", fontWeight: "var(--fw-semibold)", backgroundColor: "var(--color-ai)", color: "var(--color-white)" }}>アカウント名</TableCell>
              <TableCell sx={{ fontSize: "var(--fs-11)", fontWeight: "var(--fw-semibold)", backgroundColor: "var(--color-ai)", color: "var(--color-white)" }}>メールアドレス</TableCell>
              <TableCell sx={{ fontSize: "var(--fs-11)", fontWeight: "var(--fw-semibold)", backgroundColor: "var(--color-ai)", color: "var(--color-white)" }}>
                初期パスワード
                <Tooltip title={showPassword ? "パスワードを隠す" : "パスワードを表示"}>
                  <IconButton onClick={() => setShowPassword((prev) => !prev)} size="small" sx={{ ml: 0.5 }} data-testid="csv-account-password-toggle">
                    {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </TableCell>
              <TableCell align="center" sx={{ fontSize: "var(--fs-11)", fontWeight: "var(--fw-semibold)", backgroundColor: "var(--color-ai)", color: "var(--color-white)" }}>コピー</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {accountList.map((acc, i) => (
              <TableRow key={"csvAccount" + i}>
                <TableCell sx={{ fontSize: "var(--fs-12)" }}>{acc.account_name}</TableCell>
                <TableCell sx={{ fontSize: "var(--fs-12)" }}>{acc.email}</TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: "var(--fs-12)" }}>
                  {acc.temp_password
                    ? (showPassword ? acc.temp_password : "●●●●●●●●")
                    : "-"}
                </TableCell>
                <TableCell align="center" sx={{ fontSize: "var(--fs-12)" }}>
                  <Tooltip title="アカウント情報をコピー">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const text = `アカウント名: ${acc.account_name}\nメールアドレス: ${acc.email}\n初期パスワード: ${acc.temp_password || "なし"}`;
                        navigator.clipboard.writeText(text);
                      }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <PoButton
        variant="secondary"
        startIcon={<ContentCopyIcon />}
        fullWidth
        sx={{ mt: 1 }}
        data-testid="csv-account-copy-all"
        onClick={() => {
          const text = accountList.map((acc) =>
            `アカウント名: ${acc.account_name}\nメールアドレス: ${acc.email}\n初期パスワード: ${acc.temp_password || "なし"}`
          ).join("\n---\n");
          navigator.clipboard.writeText(text);
        }}
      >
        すべてコピー
      </PoButton>
    </PoModalShell>
  );
}
